import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Link
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import LaunchIcon from '@mui/icons-material/Launch';
import HomeIcon from '@mui/icons-material/Home';
import LocalGovIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import ParkIcon from '@mui/icons-material/Park';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import ElectricIcon from '@mui/icons-material/BoltOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const Links = () => {
  const resourceCategories = [
    {
      title: "Local Government",
      icon: <LocalGovIcon color="primary" fontSize="large" />,
      links: [
        { name: "City of Chelsea", url: "https://cityofchelsea.com/", description: "Official website for the City of Chelsea, AL" },
        { name: "Shelby County", url: "https://www.shelbyal.com/", description: "Shelby County government website" },
        { name: "Chelsea Business Alliance", url: "https://chelseabusinessalliance.com/", description: "Local business community and resources" }
      ]
    },
    {
      title: "Schools & Education",
      icon: <SchoolIcon color="primary" fontSize="large" />,
      links: [
        { name: "Shelby County Schools", url: "https://www.shelbyed.k12.al.us/", description: "Shelby County School District website" },
        { name: "Chelsea High School", url: "https://www.shelbyed.k12.al.us/schools/chs/index.htm", description: "Chelsea High School website" },
        { name: "Chelsea Middle School", url: "https://www.shelbyed.k12.al.us/schools/cms/index.htm", description: "Chelsea Middle School website" },
        { name: "Chelsea Park Elementary", url: "https://www.shelbyed.k12.al.us/schools/cpes/index.htm", description: "Chelsea Park Elementary School website" },
        { name: "Forest Oaks Elementary", url: "https://www.shelbyed.k12.al.us/schools/foes/index.htm", description: "Forest Oaks Elementary School website" }
      ]
    },
    {
      title: "Community Resources",
      icon: <LocalLibraryIcon color="primary" fontSize="large" />,
      links: [
        { name: "Chelsea Public Library", url: "https://www.cityofchelsea.com/242/Library", description: "Chelsea Public Library information and programs" },
        { name: "Chelsea Community Center", url: "https://cityofchelsea.com/205/Community-Center", description: "Community center activities and rental information" },
        { name: "Chelsea Neighborhood Contacts", url: "/documents/Chelsea_Neighborhood_Contacts.pdf", description: "Important contacts for Chelsea neighborhoods", isPdf: true }
      ]
    },
    {
      title: "Parks & Recreation",
      icon: <ParkIcon color="primary" fontSize="large" />,
      links: [
        { name: "Chelsea Parks & Recreation", url: "https://cityofchelsea.com/223/Parks-Recreation", description: "Information about local parks and recreational activities" },
        { name: "Chelsea Sports Complex", url: "https://cityofchelsea.com/223/Parks-Recreation", description: "Sports facilities and events" },
        { name: "Chelsea Youth Club", url: "https://www.chelseayouthclub.com/", description: "Youth sports programs and registration" }
      ]
    },
    {
      title: "Utilities & Services",
      icon: <ElectricIcon color="primary" fontSize="large" />,
      links: [
        { name: "Alabama Power", url: "https://www.alabamapower.com/", description: "Electricity service provider" },
        { name: "Spire Gas", url: "https://www.spireenergy.com/", description: "Natural gas service provider" },
        { name: "Spectrum", url: "https://www.spectrum.com/", description: "Cable, internet, and phone services" },
        { name: "AT&T", url: "https://www.att.com/", description: "Internet, TV, and phone services" },
        { name: "Republic Services", url: "https://www.republicservices.com/", description: "Trash and recycling services" },
        { name: "Pelham Water Works", url: "https://pelhamalabama.gov/202/Water-Sewer", description: "Water and Sewer Services"}
      ]
    },
    {
      title: "Emergency Services",
      icon: <LocalPoliceIcon color="primary" fontSize="large" />,
      links: [
        { name: "Chelsea Fire & Rescue", url: "https://cityofchelsea.com/170/Fire-Department", description: "Fire and emergency medical services" },
        { name: "Shelby County Sheriff's Office", url: "https://www.shelbyso.com/", description: "Law enforcement for Shelby County" },
        { name: "Grandview Medical Center", url: "https://www.grandviewhealth.com/", description: "Nearby hospital and emergency care" }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Helpful Links & Resources | Brynleigh Estates HOA</title>
        <meta name="description" content="A collection of helpful links and resources for Brynleigh Estates residents in Chelsea, Alabama, including local government, schools, utilities, and emergency services." />
        <link rel="canonical" href="https://brynleighestates.com/links" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/links" />
        <meta property="og:title" content="Helpful Links & Resources | Brynleigh Estates HOA" />
        <meta property="og:description" content="A collection of helpful links and resources for Brynleigh Estates residents, including local government, schools, utilities, and emergency services." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Helpful Links & Resources | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Helpful links and resources for Brynleigh Estates residents in Chelsea, Alabama." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title="Helpful Links"
        subtitle="Resources for our community"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="medium" component="h1" align="center">
            Community Resources
          </Typography>
          
          <Typography variant="body1" paragraph align="center" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
            We've compiled a collection of helpful links and resources to assist Brynleigh Estates residents. 
            From local government information to schools and utilities, find what you need below.
          </Typography>
          
          <Divider sx={{ mb: 6 }} />
          
          <Grid container spacing={4}>
            {resourceCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{ 
                      p: 3, 
                      height: '100%',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2 }}>
                        {category.icon}
                      </Box>
                      <Typography variant="h5" component="h2">
                        {category.title}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <List>
                      {category.links.map((link, linkIndex) => (
                        <ListItem 
                          key={linkIndex} 
                          sx={{ 
                            px: 0, 
                            py: 1.5,
                            borderBottom: linkIndex < category.links.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="medium">
                                {link.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {link.description}
                              </Typography>
                            }
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={link.isPdf ? <PictureAsPdfIcon /> : <LaunchIcon />}
                          >
                            {link.isPdf ? 'PDF' : 'Visit'}
                          </Button>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
          
          <Box sx={{ mt: 8, mb: 4 }}>
            <Typography variant="h5" gutterBottom component="h2" align="center">
              Homeowner Services
            </Typography>
            <Divider sx={{ mb: 4 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <HomeIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" component="h3" align="center" gutterBottom>
                      HOA Documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Access important community documents, covenants, and bylaws.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button 
                      variant="contained" 
                      size="small" 
                      color="primary"
                      component="a"
                      href="/covenants"
                    >
                      View Documents
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <ParkIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" component="h3" align="center" gutterBottom>
                      Community Photos
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Browse photos from community events and neighborhood scenes.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button 
                      variant="contained" 
                      size="small" 
                      color="primary"
                      component="a"
                      href="/photos"
                    >
                      View Gallery
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <LocalGovIcon color="primary" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography variant="h6" component="h3" align="center" gutterBottom>
                      Board Members
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Meet the HOA board members and committee volunteers.
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button 
                      variant="contained" 
                      size="small" 
                      color="primary"
                      component="a"
                      href="/board-members"
                    >
                      Meet The Board
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            </Grid>
          </Box>
          
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="body1">
              Have a suggestion for a helpful link or resource we should add? Email us at{' '}
              <Link href="mailto:board@brynleighestates.com" color="primary">
                board@brynleighestates.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Links; 